import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { IconBack, IconClockRecord, IconInfo, IconSliders, IconSwap } from "../control/ui/icons";
import { INFINITY_AUTO_SCROLL_CHOICES_S, type GoonSession, type InfinityAutoScrollSec } from "@gooner/shared";
import { formatElapsed } from "../lib/elapsed";
import { useServerClockOffset } from "../lib/serverClock";
import { MENU_ROW, MenuRowIcon } from "../components/SiteBar";
import { cx } from "../control/ui/primitives";
import type { NowShowingLabel } from "./ScreenControls";
import { WallGlyph } from "../components/WallGlyph";
import { InfinityGlyph } from "../components/InfinityGlyph";

/**
 * The phone's controls for a screen.
 *
 * WHY THIS EXISTS. Below `sm:` the site bar folds to sound, fullscreen and a code chip,
 * and everything else goes with it: Now Showing, the ⓘ, the chevrons, Edit, screen
 * settings and the goon session. `ScreenControls` argued that was right — "a phone playing
 * a view is a screen being WATCHED, not driven", with the remote carrying the rest. That
 * holds for a television with a phone beside it and fails for ONE PERSON WITH ONE PHONE,
 * where there is no second device and the fold is the whole product. Edit, the gear and
 * the ⓘ had no second route anywhere, not even on the remote.
 *
 * NOT A SECOND BAR, and that is the lesson of the first attempt. Two full-width slabs
 * floating over the glass covered the marquee square's top edge and the whole bottom row
 * of tiles, and on the wall the lower one held two controls with a hand's width of dead
 * space between them — bar-shaped chrome for a button's worth of content. At 320px, where
 * the site bar already wraps to 83px, that was most of the screen.
 *
 * So nothing here spans the viewport. Two clusters that hug their own contents:
 *
 *   · a CHIP at the top, under the nav bar, saying what is playing and stepping through it
 *   · a SESSION TAB hung under that chip while a session runs — timer, views, +1 Edge —
 *     so the one thing pressed repeatedly sits with the one piece of chrome already there
 *
 * and everything rare behind `⋯`, in the sheet the remote and Browse already use. A control
 * earns a place on the glass by being pressed mid-watch; everything else is a tap deeper
 * and costs no pixels at all.
 *
 * SOUND AND FULLSCREEN ARE NOT HERE. Neither folds from the bar, so both are already a
 * reach away and a second copy would be two buttons for one state. Fullscreen was briefly
 * put in the sheet "for where the bar cannot show it", which is nonsense on inspection:
 * the bar omits that button only where the browser has no Fullscreen API (iPhone Safari),
 * and there is nothing for a sheet to offer either.
 *
 * IT COSTS NOTHING WHEN IDLE. The caller fades it with `useScreenIdle`, the hook the
 * wall's price badges already fade on. While faded it is `pointer-events-none`: an
 * invisible cluster that still swallowed taps meant for a tile is worse than no cluster.
 *
 * EXCEPT THE ⓘ, ON TOUCH (2026-10-21). A mouse brings the chip back by moving; a finger
 * has nothing to move, so on a touch screen the one door to "what is this view?" vanished
 * four seconds in and never hinted it was there. On `pointer-coarse` the chip still fades
 * — its ground, the name, the chevrons, the session tab, the bottom row — but the ⓘ stays
 * where it was, dimmed (`opacity-50`, this codebase's dimmed control) and lifted off the
 * picture with the glass glyphs' shadow, and it does not fade: it switches. A mouse keeps
 * the whole-chip fade exactly as it was.
 *
 * NOT ON AN INFINITE SCROLL (owner, 2026-10-31): `infoFades`. A scroll is the picture and
 * nothing else, and any tap there brings the chip back, so the ⓘ goes with the rest. So
 * does the pill's hit area: invisible buttons must not eat the top of a double tap.
 *
 * EVERY WIDTH'S NOW (2026-09-09). The bar used to carry its own "Now Showing" bubble and
 * a gold "Goon" session pill above `lg:`, and this floated below it; the desk gets this
 * too — the chip over the picture it names, the running session's +Edge at the bottom
 * left, fading on idle, the one component — and the bar's own spellings are gone.
 *
 * Presentational, like the cluster it mirrors: every action is a prop.
 */
/**
 * THE CHIP'S SHADOW ON THE TILES. It floats centred under the bar, so a tile in the top
 * row whose span crosses the middle has its hover chrome — labels top-left, sound/sync/
 * loop top-right — under the chip and unreachable (seen on Tit Ladder: the volume
 * slider half under "Tit Ladder ⓘ"). DisplayApp asks here which tiles that is and pushes
 * their chrome down by the chip's row. The row is the layer's `p-2` plus the pill's
 * `h-10` (48px) with a breath; the half-width is the widest name the pill grows to.
 */
export const CHIP_ROW_PX = 52;
const CHIP_HALF_PX = 170;
export function chipCovers(region: { x: number; y: number; w: number }, glassWidth: number): boolean {
  if (region.y > 0.02) return false;
  const half = Math.min(0.5, CHIP_HALF_PX / Math.max(1, glassWidth));
  return region.x < 0.5 + half && region.x + region.w > 0.5 - half;
}

/**
 * THE SESSION TAB'S HEIGHT, added to CHIP_ROW_PX while a session runs: the tab hangs off
 * the chip, so the chip's shadow on the top row grows by it. (2026-09-18. The running
 * session used to be a pill floating bottom-LEFT, which on a phone reached the bottom
 * row's fap crowns — "the clip's fap crown gets covered by the session HUD". Rather than
 * lifting crowns out from under it, the HUD moved: everything about the session now hangs
 * under the one piece of chrome the glass already wears.)
 */
export const SESSION_TAB_PX = 26;

export function ScreenThumbBar({
  label,
  onLabelPress,
  onInfo,
  onPrev,
  onNext,
  onEdit,
  editBusy = false,
  onSettings,
  session,
  onEdge,
  onStartSession,
  allowance,
  idle = false,
  infoFades = false,
}: {
  label?: NowShowingLabel | null;
  onLabelPress?: (() => void) | null;
  onInfo?: (() => void) | null;
  onPrev?: (() => void) | null;
  onNext?: (() => void) | null;
  onEdit?: (() => void) | null;
  editBusy?: boolean;
  onSettings?: (() => void) | null;
  /** The running session, for the tab under the chip; null or absent draws no tab. */
  session?: GoonSession | null;
  /** +1 Edge, the tab's one button. */
  onEdge?: (() => void) | null;
  /** The daily wall allowance's gauge (2026-09-10), while the meter is filling — the
   *  host mounts the SPENT form itself, persistently, because a shrunk wall must say
   *  why even after the chrome fades. */
  allowance?: ReactNode;
  /**
   * Start a session, for the sheet's own row.
   *
   * The handler rather than the `session` node, because in there it is one choice among
   * four and should look like the other three. Dropping the node in gave the sheet a gold
   * pill twice the height of its neighbours — the control shouting across a room it is no
   * longer in. On the glass, while running, it keeps its own shape; that one IS loud on
   * purpose.
   */
  onStartSession?: (() => void) | null;
  /** The Infinite Scroll's auto-scroll — present on the scroll alone. Read by
   *  ScreenMenuItems (the ☰ row); the glass draws nothing for it. */
  autoScroll?: AutoScrollControl | null;
  /** Build a Slut alone: throw the picks away and play the rounds again (a ☰ row). */
  onStartOver?: (() => void) | null;
  idle?: boolean;
  /** Touch, idle: the ⓘ fades with the chip instead of staying (see above). */
  infoFades?: boolean;
}) {
  if (!label) return null;
  const hidden = idle;
  // THE TOUCH FADE (see "EXCEPT THE ⓘ" above). On a mouse the layer fades as one; on
  // `pointer-coarse` the layer holds and each part but the ⓘ fades itself, so the ⓘ can
  // outlive the chip it sits in — an ancestor's opacity would take it down too. Every
  // class here is `pointer-coarse:`, so on a mouse these parts are exactly what they were.
  const fade = cx("pointer-coarse:transition-opacity pointer-coarse:duration-300", hidden && "pointer-coarse:opacity-0");

  return (
      <div
        className={`pointer-events-none absolute inset-0 z-30 transition-opacity duration-300 ${
          hidden ? "opacity-0 pointer-coarse:opacity-100" : "opacity-100"
        }`}
        aria-hidden={hidden || undefined}
      >
        {/* WHAT IS PLAYING — a chip, not a bar. `justify-center` on a full-width row so
            the chip itself is only as wide as its name, centred under the nav bar, with
            the glass either side of it left alone. It gets the full width to be narrow
            IN: a `px-14` reserve here once kept it clear of a floating menu button, and
            when that button moved into the bar the reserve stayed behind and clipped
            "Community Wall" by 36px on a 320px screen.

            The chevrons ride at both ends at EVERY width this renders at, unlike the
            bar's own (which are `lg:`) — a phone has no arrow keys, so on the bar's terms
            a phone could not step through views at all. */}
        {label ? (
          <div className="absolute inset-x-0 top-0 flex flex-col items-center p-2">
            <div
              className={cx(
                "pointer-events-auto relative z-10 flex max-w-full items-center overflow-hidden rounded-full border border-white/15 bg-[var(--color-ink)]/85 backdrop-blur pointer-coarse:border-0 pointer-coarse:bg-transparent pointer-coarse:p-px pointer-coarse:backdrop-blur-none",
                hidden && infoFades && "pointer-coarse:pointer-events-none",
              )}
            >
              {/* On touch the pill's ground moves to a layer of its own, so it can fade while
                  the ⓘ in front of it stays. Its border lands in the pill's `p-px`, where the
                  pill's own 1px border was, so the pill measures exactly what it did. On a
                  mouse this layer is not drawn and the pill paints its own ground, as ever. */}
              <span
                aria-hidden
                data-chip-ground=""
                className={cx(
                  "pointer-events-none absolute inset-0 -z-10 hidden rounded-full border border-white/15 bg-[var(--color-ink)]/85 backdrop-blur pointer-coarse:block",
                  fade,
                )}
              />
              {onPrev ? (
                <button
                  type="button"
                  onClick={onPrev}
                  aria-label="Play the previous view"
                  className={cx("flex h-10 w-9 shrink-0 items-center justify-center text-zinc-400 active:bg-white/15", fade)}
                >
                  <IconBack width={14} height={14} />
                </button>
              ) : null}
              {onLabelPress ? (
                <button
                  type="button"
                  onClick={onLabelPress}
                  aria-label="Change what's playing"
                  className={cx("flex min-w-0 items-center gap-1.5 px-1.5 py-1.5 text-[13px] active:bg-white/15", fade)}
                >
                  <Body label={label} />
                </button>
              ) : (
                <span className={cx("flex min-w-0 items-center gap-1.5 px-1.5 py-1.5 text-[13px]", fade)}>
                  <Body label={label} />
                </span>
              )}
              {/* ⓘ READS AS PART OF THE NAME. "Community Wall ⓘ" is one phrase — the
                  button asks about the words to its left — so no divider and no cell of
                  its own, which said instead that some fourth thing lived here. Still a
                  SIBLING of the label button: HTML forbids a button inside a button, and
                  looking joined is not the same as being nested. */}
              {onInfo ? (
                <button
                  type="button"
                  onClick={onInfo}
                  aria-label="What is this view?"
                  data-info-door=""
                  className={cx(
                    "-ml-1 flex h-10 shrink-0 items-center px-2 text-zinc-500 active:text-zinc-200",
                    // Touch, idle: no fade, no ground — so a lighter glyph at half strength
                    // with the shadow the glass's other glyphs wear (FapBar, the fap crown).
                    infoFades
                      ? fade
                      : hidden &&
                          "pointer-coarse:text-zinc-200 pointer-coarse:opacity-50 pointer-coarse:drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]",
                  )}
                >
                  <IconInfo width={13} height={13} />
                </button>
              ) : null}
              {onNext ? (
                <button
                  type="button"
                  onClick={onNext}
                  aria-label="Play the next view"
                  className={cx("flex h-10 w-9 shrink-0 items-center justify-center border-l border-white/10 text-zinc-400 active:bg-white/15", fade)}
                >
                  <IconBack width={14} height={14} className="rotate-180" />
                </button>
              ) : null}
            </div>
            {/* THE RUNNING SESSION hangs under the chip — see SessionTab. Idle there is
                nothing here: starting one is the record dot in the bar. */}
            <div className={fade}>{session ? <SessionTab session={session} onEdge={onEdge ?? null} /> : null}</div>
          </div>
        ) : null}

        {/* The bottom row holds the allowance gauge alone now, bottom-left, in the thumb's
            arc; the running session left it for the chip (SessionTab). */}
        <div className={cx("absolute inset-x-0 bottom-0 flex items-center gap-2 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]", fade)}>
          {allowance ? (
            <div className="pointer-events-auto flex items-center rounded-full border border-white/15 bg-[var(--color-ink)]/85 px-3 py-1.5 backdrop-blur">
              {allowance}
            </div>
          ) : null}
        </div>
      </div>
  );
}

/**
 * THE SESSION TAB: a small trapezoid hung off the bottom of the view-name chip while a
 * session runs — attached to it, not floating anywhere of its own — carrying, in very
 * small type, only the live timer, the view count, the edge count and a yellow "Edge". Stop is not
 * here: the bar's record dot is the stop while a session runs (ScreenControls). A tab
 * rather than a pill because it is a reading about the thing above it, the way a
 * bookmark is about its page; the trapezoid's narrowing bottom says "under", the way a
 * dropdown's does. Tucked `-mt-1.5` behind the chip (which is `z-10`) so the pill's
 * curve sits on top and the tab appears to grow out of it.
 *
 * Same clock as the pill it replaces: the server's, through the measured offset.
 */
function SessionTab({ session, onEdge }: { session: GoonSession; onEdge: (() => void) | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const clockOffset = useServerClockOffset();
  const elapsedMs = now + clockOffset - session.startedAt;
  return (
    <div
      data-session-tab=""
      className="pointer-events-auto -mt-1.5 flex items-center gap-2 bg-[var(--color-ink)]/85 px-5 pb-1 pt-2.5 text-[10px] leading-none text-zinc-300 backdrop-blur"
      style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 10px) 100%, 10px 100%)" }}
    >
      <span className="relative flex h-1.5 w-1.5 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--color-accent)] opacity-60" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--color-accent)]" />
      </span>
      <span className="font-mono tabular-nums text-zinc-100">{formatElapsed(elapsedMs)}</span>
      <span className="text-zinc-400" title="Clips viewed this session">
        <span aria-hidden>👁</span> {session.views}
      </span>
      {/* The edge count beside the views, same small grey (owner, 2026-09-19): the two
          readings of how far the session has gone sit together, and the button below is
          the act alone — "Edge", not "+1 Edge". 💦 is the edge glyph the clip stats use. */}
      <span className="text-zinc-400" title="Edges this session">
        <span aria-hidden>💦</span> {session.edges}
      </span>
      {onEdge ? (
        <button
          type="button"
          onClick={onEdge}
          title="Add an edge"
          className="rounded-full bg-[var(--color-accent)] px-2 py-0.5 text-[10px] font-bold leading-none text-black transition-colors hover:bg-[var(--color-accent)]/85"
        >
          Edge
        </button>
      ) : null}
    </div>
  );
}

/** The label's own contents, shared by its button and its plain-fact forms.
 *
 *  NO "NOW" PREFIX. The bar's pill says "Now Showing:" because it sits in a row of
 *  unrelated controls and has to announce what kind of fact it is. Here it is alone under
 *  the nav bar, wearing the view's own icon, with a chevron on each side — there is
 *  nothing else it could be, and the word was a label for a label. */
function Body({ label }: { label: NowShowingLabel }) {
  return (
    <>
      {label.wall ? (
        <WallGlyph wallId={label.wall} className="shrink-0" />
      ) : label.infinity ? (
        <InfinityGlyph infinityId={label.infinity} className="shrink-0" />
      ) : label.icon ? (
        <span aria-hidden className="shrink-0">
          {label.icon}
        </span>
      ) : null}
      <span className="truncate font-semibold text-zinc-100">{label.name}</span>
    </>
  );
}

/**
 * A screen's own rows for the site bar's ☰ menu, at every width (the gear was a bar
 * button above `lg:` until 2026-09-09; a once-a-sitting act with words on it is a row).
 *
 * WHY NOT ON THE GLASS. Everything here was tried as a floating button first, and there is
 * nowhere on a wall to put one. A tile wears its fap crown at bottom-right, its hover
 * controls at top-right, its labels at top-left and — on the wall — a holder/price badge
 * spanning the whole bottom edge. Every corner of every tile is already spoken for, so a
 * floating control does not sit BESIDE the wall's chrome, it sits ON it, and swallows the
 * taps meant for whatever it landed on. Two positions were tried and each collided with a
 * different thing.
 *
 * The bar already owns the answer. `SiteBar`'s `menu` prop is documented as "where a
 * page's controls go when a phone cannot fit them in the bar", the ☰ that renders it is
 * already `sm:hidden`, and it costs the wall nothing — which is the actual goal here: a
 * screen is for showing as much of the regions as possible.
 *
 * These are the once-a-sitting acts. What stays on the glass is only what is pressed
 * mid-watch: the view's name, its chevrons, and a running session's +Edge.
 *
 * NO "edit" ROW EITHER — that is a pencil in the bar now, next to the record dot, in the
 * space the code chip's mask button gave up.
 *
 * NO "start a session" ROW EITHER: it is a record dot in the bar now (ScreenControls),
 * one tap with nothing to read. A menu row would be a second door to the same act.
 *
 * NO "What is this?" ROW. The ⓘ already sits against the view's name on the glass, where
 * it is a question about the words beside it; a second copy in a list here would be the
 * same act twice, and the one in the list could not say what it was asking about.
 */
/** The auto-scroll setting as the ☰ row sees it: the wait in seconds, and the setter. */
export interface AutoScrollControl {
  sec: InfinityAutoScrollSec;
  onChange: (sec: InfinityAutoScrollSec) => void;
}

/** "Off" for 0, "10s" for 10 — the row's word. */
export const autoScrollLabel = (sec: number): string => (sec === 0 ? "Off" : `${sec}s`);

/** The wait after this one: Off → 5s → 10s → 20s → 30s → 60s → Off. */
export const nextAutoScrollSec = (sec: InfinityAutoScrollSec): InfinityAutoScrollSec => {
  const i = (INFINITY_AUTO_SCROLL_CHOICES_S as readonly number[]).indexOf(sec);
  return INFINITY_AUTO_SCROLL_CHOICES_S[(i + 1) % INFINITY_AUTO_SCROLL_CHOICES_S.length]!;
};

export function ScreenMenuItems({
  onLabelPress,
  onSettings,
  autoScroll,
  onStartOver,
}: {
  onLabelPress?: (() => void) | null;
  onSettings?: (() => void) | null;
  autoScroll?: AutoScrollControl | null;
  /** Build a Slut alone: throw the picks away and play the rounds again (a ☰ row). */
  onStartOver?: (() => void) | null;
}) {
  // The menu's own row shape, from the menu itself, so a screen's rows and the account's
  // read as one list rather than two that happen to share a popover.
  const row = cx(MENU_ROW, "text-zinc-300");
  /* Both wear a glyph, like every other row in this menu — one row without one leaves a
     hole in the icon column, which is more noticeable than the icons are. Settings takes
     the sliders the bar's own settings button already wears; switching takes the swap
     arrows rather than the layout grid, which the Views row up the list is using. */
  return (
    <>
      {onLabelPress ? (
        <button type="button" role="menuitem" onClick={onLabelPress} className={row}>
          <MenuRowIcon>
            <IconSwap />
          </MenuRowIcon>
          Switch view
        </button>
      ) : null}
      {onSettings ? (
        <button type="button" role="menuitem" onClick={onSettings} className={row}>
          <MenuRowIcon>
            <IconSliders />
          </MenuRowIcon>
          Screen settings
        </button>
      ) : null}
      {/* AUTO-SCROLL (2026-09-14), the Infinite Scroll alone: ONE row that is a setting,
          not a door — each press steps the wait, Off → 5s → … → 60s → Off (the owner's
          call over a strip of chips). The value sits at the row's end in the accent so
          the state reads at a glance, and the click stays in the row: the menu closes on
          any click inside it, and stepping a wait is not leaving. */}
      {/* START OVER (Build a Slut, 2026-09-19): the once-a-sitting act of throwing her
          away and picking again. A row, like every other once-a-sitting act. */}
      {onStartOver ? (
        <button type="button" role="menuitem" onClick={onStartOver} className={row}>
          <MenuRowIcon>
            <span aria-hidden>↺</span>
          </MenuRowIcon>
          Start over
        </button>
      ) : null}
      {autoScroll ? (
        <button
          type="button"
          role="menuitem"
          aria-label={`Auto-scroll: ${autoScrollLabel(autoScroll.sec)} — press for the next wait`}
          onClick={(e) => {
            e.stopPropagation();
            autoScroll.onChange(nextAutoScrollSec(autoScroll.sec));
          }}
          className={cx(row, "justify-between")}
        >
          <span className="flex items-center gap-2.5">
            <MenuRowIcon>
              <IconClockRecord />
            </MenuRowIcon>
            Auto-scroll
          </span>
          <span
            data-auto-scroll={autoScroll.sec}
            className={cx(
              "rounded-full px-2 py-0.5 text-[11px] font-semibold",
              autoScroll.sec === 0 ? "bg-white/10 text-zinc-400" : "bg-[var(--color-accent)] text-black",
            )}
          >
            {autoScrollLabel(autoScroll.sec)}
          </span>
        </button>
      ) : null}
    </>
  );
}
